import {
  Component,
  ChangeDetectionStrategy,
  ElementRef,
  OnInit,
  OnDestroy,
  inject,
  input,
  output,
  signal,
  computed,
} from '@angular/core';

import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { EMPTY, Observable, Subscription, forkJoin, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { ExecPlan, Workflow } from '@app/shared/interfaces';
import {
  WorkflowDeletionImpact,
  WorkflowService,
} from '../../shared/services/workflow/workflow.service';
import { WorkflowCreateDialogData } from './workflow-create-dialog/workflow-create-dialog.component';
import { DialogService } from '../../shared/services/dialog/dialog.service';
import { AppStateService } from '../../shared/services/app-state/app-state.service';
import { MessageService } from '../../shared/services/message/message.service';
import { AppEventBus } from '../../shared/services/events/app-events';
import { ProjectService } from '../../shared/services/project/project.service';
import { ExecutionPlanService } from '../../shared/services/execution-plan/execution-plan.service';
import { VersionService } from '../../shared/services/version/version.service';
import { PermissionId } from '../../shared/constants/permissions.constants';
import { RELOAD_NOTEBOOKS } from '../../shared/constants/general.constants';
import { DateFormattingHelper } from '../../shared/helpers/date-formatting.helper';
import { ToastrService } from 'ngx-toastr';
import {
  ServerError,
  historyJobCount,
  serverErrorCode,
} from '../../shared/interfaces/server-error.interface';

export interface WorkflowSelectEvent {
  workflow: Workflow;
  workflowName: string;
}

/** What a workflow delete acts on (EHQ-512). */
interface WorkflowDeleteTarget {
  projectId: number;
  versionId: number;
  workflowId: number;
  /** The routines connected to the workflow by id or by name. */
  routines: ExecPlan[];
}

/**
 * Why a routine delete failed: the server's envelope message, else a plain
 * string body, else the error's own message (a network failure has only
 * that). Undefined when there's nothing readable.
 */
function routineDeleteMessage(err: { error?: unknown; message?: unknown } | null | undefined): string | undefined {
  const body = err?.error;
  const envelope = (body as Partial<ServerError> | undefined)?.message;
  if (typeof envelope === 'string' && envelope) return envelope;
  if (typeof body === 'string' && body) return body;
  return typeof err?.message === 'string' && err.message ? err.message : undefined;
}

/**
 * Whether a failed deletion-impact call means the server predates the
 * endpoint: a 501, or a 404 without the server's error envelope (a missing
 * workflow is a 404 that carries one).
 */
function lacksDeletionImpact(err: { status?: unknown; error?: unknown } | null | undefined): boolean {
  return err?.status === 501 || (err?.status === 404 && serverErrorCode(err) === undefined);
}

@Component({
  selector: 'app-workflow-dropdown',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:click)': 'onDocumentClick($event)',
    '(keydown.escape)': 'onEscapeKey()',
  },
  templateUrl: './workflow-dropdown.component.html',
  styleUrl: './workflow-dropdown.component.scss',
  imports: [MatIconModule, MatButtonModule, MatDialogModule, MatTooltipModule],
})
export class WorkflowDropdownComponent implements OnInit, OnDestroy {
  private elementRef = inject(ElementRef);

  // Signal inputs
  projectId = input<string | undefined>(undefined);
  selectedWorkflowId = input<number | undefined>(undefined);
  currentWorkflow = input<string>('New Workflow');
  disabled = input<boolean>(false);

  // Signal outputs
  workflowSelected = output<WorkflowSelectEvent>();
  newWorkflowRequested = output<void>();
  workflowDeleted = output<number>();

  // Internal state
  workflows = signal<Workflow[]>([]);
  isOpen = signal<boolean>(false);
  private subscriptions = new Subscription();
  private notebookService = inject(WorkflowService);
  private dialogService = inject(DialogService);
  private appStateService = inject(AppStateService);
  private messageService = inject(MessageService);
  private appEventBus = inject(AppEventBus);
  private projectService = inject(ProjectService);
  private executionPlanService = inject(ExecutionPlanService);
  private versionService = inject(VersionService);
  private toastr = inject(ToastrService);

  // Computed signals
  isProjectOwner = computed(() => this.appStateService.isProjectOwner());
  isHeadVersion = computed(() => this.appStateService.isHeadVersion());
  isCurrentEditor = computed(() => this.appStateService.isCurrentEditor());
  activeWorkflow = computed(() => this.appStateService.workflow());
  activeWorkflowId = computed(() => this.appStateService.workflowId());

  /** True when the current user can delete workflows (Admin/Owner + HEAD + holds lock). */
  canDeleteWorkflow = computed(
    () =>
      this.isHeadVersion() &&
      this.isCurrentEditor() &&
      (this.isProjectOwner() ||
        this.projectService.getCurrentUserHasProjectPermission(
          PermissionId.ADMIN,
        )),
  );

  /**
   * True when the current user can create workflows.
   *
   * SCRUM-542: previously the "New Workflow" button was gated on
   * `isProjectOwner() && isHeadVersion()`, which strictly requires the project
   * creator (`user.userId === project.userId`). Admins holding the lock were
   * silently blocked even though they can delete workflows. Aliased to
   * `canDeleteWorkflow` so the create and delete gates cannot drift apart.
   */
  canCreateWorkflow = this.canDeleteWorkflow;

  ngOnInit(): void {
    this.loadWorkflows();
    this.setupMessageSubscription();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private setupMessageSubscription(): void {
    const messageSub = this.messageService
      .getMessage()
      .subscribe((message: any) => {
        if (message?.text === RELOAD_NOTEBOOKS) {
          const projectId = message?.data?.projectId;
          if (!projectId || projectId === this.projectId()) {
            this.loadWorkflows();
          }
        }
      });
    this.subscriptions.add(messageSub);

    // Server-driven workflow-list refresh now arrives via the typed
    // AppEventBus (SCRUM-750). The seam only fires for the watched project,
    // so no project match is needed here.
    this.subscriptions.add(
      this.appEventBus
        .on('reloadWorkflows')
        .subscribe(() => this.loadWorkflows()),
    );
  }

  private loadWorkflows(): void {
    const projectId = this.projectId();
    if (!projectId) {
      return;
    }

    const parsedProjectId = parseInt(projectId);
    const versionId = this.appStateService.versionId();

    if (!parsedProjectId || !versionId) {
      return;
    }

    const cachedWorkflows: Workflow[] = this.notebookService.getNotebookList();
    this.updateWorkflowsFromWorkflows(cachedWorkflows);

    const fetchSub = this.notebookService
      .getAllWorkflows(parsedProjectId, versionId)
      .subscribe({
        next: (workflows) => {
          if (workflows && Array.isArray(workflows)) {
            this.updateWorkflowsFromWorkflows(workflows);
          }
        },
        error: () => {
          // Fallback to cached data if API fails - silently handled
        },
      });

    this.subscriptions.add(fetchSub);
  }

  private updateWorkflowsFromWorkflows(workflows: Workflow[]): void {
    const processedWorkflows = workflows
      .filter((workflow) => workflow.workflowId && workflow.name)
      .map((workflow) => ({
        workflowId: workflow.workflowId,
        projectId: parseInt(this.projectId() || '0'),
        versionId: workflow.versionId || this.appStateService.versionId(),
        name: workflow.name,
        defaultState: workflow.defaultState || '',
        headlessOnly: workflow.headlessOnly ?? false,
        description: workflow.description || '',
        createdAt: workflow.createdAt || new Date().toISOString(),
        isActive: workflow.isActive !== undefined ? workflow.isActive : true,
      }));

    this.workflows.set(processedWorkflows);

    if (!this.selectedWorkflowId() && processedWorkflows.length > 0) {
      const selectedNotebook = this.notebookService.getSelectedNotebook();
      if (
        selectedNotebook &&
        selectedNotebook.workflowId &&
        selectedNotebook.name
      ) {
        // Parent component should handle this
      }
    }
  }

  toggleDropdown(): void {
    if (this.disabled()) return;

    if (!this.isOpen()) {
      this.loadWorkflows();
    }

    this.isOpen.update((open) => !open);
  }

  closeDropdown(): void {
    this.isOpen.set(false);
  }

  selectWorkflow(workflow: Workflow): void {
    const workflowName = workflow.name;
    this.workflowSelected.emit({
      workflow,
      workflowName,
    });
    this.closeDropdown();
  }

  createNewWorkflow(): void {
    if (!this.canCreateWorkflow()) return;

    const projectId = this.projectId();
    if (!projectId) {
      return;
    }

    const numericProjectId = parseInt(projectId, 10);
    if (isNaN(numericProjectId)) {
      return;
    }

    const dialogData: WorkflowCreateDialogData = {
      projectId: projectId,
      existingWorkflows: this.workflows(),
    };

    const dialogSub = this.dialogService
      .workflowCreateDialog(dialogData)
      .subscribe((result: any) => {
        if (result) {
          if (result.workflow?.workflowId && result.workflow?.name) {
            const newWorkflow: Workflow = {
              workflowId: result.workflow.workflowId,
              projectId: numericProjectId,
              versionId:
                this.appStateService.versionId() ||
                result.workflow?.versionId ||
                1,
              name: result.workflow.name,
              defaultState: '',
              headlessOnly: result.workflow.headlessOnly ?? false,
              description: '',
              createdAt: new Date().toISOString(),
              isActive: true,
            };

            const existingWorkflows = this.workflows();
            const exists = existingWorkflows.some(
              (workflow) => workflow.workflowId === newWorkflow.workflowId,
            );
            if (exists) {
              this.workflows.set(
                existingWorkflows.map((workflow) =>
                  workflow.workflowId === newWorkflow.workflowId
                    ? newWorkflow
                    : workflow,
                ),
              );
            } else {
              this.workflows.set([...existingWorkflows, newWorkflow]);
            }

            // Seed full version cache so the project guard sees the new workflow
            // immediately — a plain cache clear is not enough when the server
            // full-version GET lags behind the create POST (SCRUM-96).
            const versionId = newWorkflow.versionId;
            this.projectService.seedFullVersionCacheWithWorkflow(
              numericProjectId,
              versionId,
              newWorkflow,
              this.appStateService.version() ?? undefined,
            );
            this.notebookService.invalidateWorkflowsCache(
              numericProjectId,
              versionId,
            );

            this.workflowSelected.emit({
              workflow: newWorkflow,
              workflowName: newWorkflow.name,
            });
          }

          this.newWorkflowRequested.emit();
        }
      });

    this.subscriptions.add(dialogSub);
    this.closeDropdown();
  }

  deleteWorkflow(workflow: Workflow, event: Event): void {
    event.stopPropagation();

    // Guard: only proceed when the user actually has permission
    if (!this.canDeleteWorkflow()) return;

    // Fix 5: prevent deleting the last workflow — the project guard requires at least one
    if (this.workflows().length <= 1) {
      this.toastr.error('Cannot delete the last workflow in the project.');
      return;
    }

    const projectId = parseInt(this.projectId() || '0');
    const versionId = this.appStateService.versionId();
    const workflowId = workflow.workflowId;

    if (!projectId || !versionId || !workflowId) return;

    const deleteSub = forkJoin({
      plans: this.executionPlanService.getPlans(projectId),
      published: this.latestPublishedHasWorkflow(projectId, workflow.name),
    })
      .pipe(
        catchError((err) => {
          console.error(
            'WorkflowDropdownComponent: failed to load routines before workflow delete',
            err,
          );
          this.toastr.error(
            'Failed to check connected routines. Please try again.',
          );
          return EMPTY;
        }),
        switchMap(({ plans, published }) => {
          const connectedRoutines = this.getRoutinesConnectedToWorkflow(
            plans,
            workflow,
            published,
          );
          const hasConnectedRoutines = connectedRoutines.length > 0;

          return this.dialogService
            .confirm({
              title: 'Delete Workflow',
              message: hasConnectedRoutines
                ? 'This workflow has connected routines. If you remove the workflow, these routines will also be removed. Continue?'
                : 'This workflow will be removed from the current version. It will remain in any previously published versions. Continue?',
              confirmCaption: 'Delete',
              cancelCaption: 'Cancel',
              isDestructive: true,
            })
            .pipe(
              switchMap((confirmed) => {
                if (!confirmed) {
                  return EMPTY;
                }

                return this.deleteCountingHistory({
                  projectId,
                  versionId,
                  workflowId,
                  routines: connectedRoutines,
                });
              }),
            );
        }),
      )
      .subscribe({
        next: () => {
          // Invalidate cache so the next dropdown open fetches a fresh list
          this.notebookService.invalidateWorkflowsCache(projectId, versionId);
          this.workflows.set(
            this.workflows().filter((w) => w.workflowId !== workflowId),
          );
          this.workflowDeleted.emit(workflowId);
          this.closeDropdown();
        },
        error: (err) => {
          console.error(
            'WorkflowDropdownComponent: failed to delete workflow or connected routines',
            err,
          );
          // A refusal such as a job still running is only explained in the
          // server's message.
          const serverMessage = (err?.error as Partial<ServerError> | undefined)?.message;
          this.toastr.error(
            serverMessage
              ? `Failed to delete workflow: ${serverMessage}`
              : 'Failed to delete workflow. Please try again.',
          );
        },
      });

    this.subscriptions.add(deleteSub);
  }

  /**
   * Count the history first, so the prompt gives the true total (the
   * workflow's own runs plus every job of the routines it deletes) and
   * declining it deletes nothing (EHQ-512). Only a server without the count
   * endpoint falls back to `deleteAskingOnConflict`; any other failure to
   * count reaches the error handler and deletes nothing.
   */
  private deleteCountingHistory(target: WorkflowDeleteTarget): Observable<void> {
    const { projectId, versionId, workflowId } = target;
    return this.notebookService.getDeletionImpact(projectId, versionId, workflowId).pipe(
      map((impact): WorkflowDeletionImpact | null => impact),
      catchError((err) => (lacksDeletionImpact(err) ? of(null) : throwError(() => err))),
      switchMap((impact) => {
        if (impact === null) {
          return this.deleteAskingOnConflict(target);
        }
        if (this.refuseDeletion(impact.executingCount)) {
          return EMPTY;
        }
        const proceed$ =
          impact.jobCount > 0 ? this.confirmDeleteHistory(impact.jobCount) : of(true);
        return proceed$.pipe(
          switchMap((proceed) => (proceed ? this.recheckAndDelete(target, impact) : EMPTY)),
        );
      }),
    );
  }

  /**
   * Count again before deleting: the prompt may have sat open while a job
   * started. The server deletes the workflow's routines with it, bound by id
   * or by name, in one transaction and under its own guard (EHQ-536), so a
   * refused delete removes nothing.
   */
  private recheckAndDelete(
    target: WorkflowDeleteTarget,
    impact: WorkflowDeletionImpact,
  ): Observable<void> {
    const { projectId, versionId, workflowId } = target;
    return this.notebookService.getDeletionImpact(projectId, versionId, workflowId).pipe(
      switchMap((again) => {
        if (this.refuseDeletion(again.executingCount)) {
          return EMPTY;
        }
        if (again.jobCount > impact.jobCount) {
          this.toastr.error(
            'Failed to delete workflow: it ran again while you were deciding. Please try again.',
          );
          return EMPTY;
        }
        return this.notebookService.deleteWorkflowWithVersion(
          projectId,
          versionId,
          workflowId,
          again.jobCount > 0,
        );
      }),
    );
  }

  /**
   * The path for a server without the count endpoint: delete the workflow
   * first, asking with the server's count if it refuses for history, and only
   * then the routines bound by name, which such a server leaves behind. A
   * refusal or a cancel therefore deletes nothing.
   */
  private deleteAskingOnConflict(target: WorkflowDeleteTarget): Observable<void> {
    const { projectId, versionId, workflowId } = target;
    const deleteWorkflow = (deleteHistory: boolean) =>
      this.notebookService.deleteWorkflowWithVersion(
        projectId,
        versionId,
        workflowId,
        deleteHistory,
      );
    return deleteWorkflow(false).pipe(
      catchError((err) => {
        const jobCount = historyJobCount(err);
        if (jobCount === undefined) {
          return throwError(() => err);
        }
        return this.confirmDeleteHistory(jobCount).pipe(
          switchMap((confirmed) => (confirmed ? deleteWorkflow(true) : EMPTY)),
        );
      }),
      switchMap(() => this.deleteNameBoundRoutines(target)),
    );
  }

  /**
   * On a server without the count endpoint, which predates deleting them with
   * the workflow (EHQ-536), delete the routines that named it once the
   * workflow is gone. Routines bound by id went with it. Every routine is
   * tried; one that can't be deleted is reported on its own, since the
   * workflow delete itself succeeded and the list must still drop it.
   */
  private deleteNameBoundRoutines(target: WorkflowDeleteTarget): Observable<void> {
    const { projectId, workflowId, routines } = target;
    const nameBound = routines.filter((routine) => routine.workflowId !== workflowId);
    if (nameBound.length === 0) {
      return of(undefined);
    }
    return forkJoin(
      nameBound.map((routine) =>
        this.executionPlanService.deletePlan(projectId, routine.routineId).pipe(
          map(() => null),
          catchError((err) => {
            console.error(
              `WorkflowDropdownComponent: failed to delete routine ${routine.routineId} after its workflow`,
              err,
            );
            const message = routineDeleteMessage(err);
            return of(message ? `'${routine.name}' (${message})` : `'${routine.name}'`);
          }),
        ),
      ),
    ).pipe(
      map((failures) => {
        const failed = failures.filter((f): f is string => f !== null);
        if (failed.length > 0) {
          this.toastr.error(
            `Workflow deleted, but ${failed.length === 1 ? 'routine' : 'routines'} ${failed.join(', ')} couldn't be removed. Delete ${failed.length === 1 ? 'it' : 'them'} from the routines list.`,
          );
        }
      }),
    );
  }

  /** A job still executing blocks the delete: say so, and return true. */
  private refuseDeletion(executingCount: number): boolean {
    if (executingCount === 0) {
      return false;
    }
    this.toastr.error(
      `Failed to delete workflow: ${executingCount} job(s) on this workflow or its routines are still executing; wait for them or cancel them before deleting.`,
    );
    return true;
  }

  /**
   * Deleting the workflow also deletes `jobCount` jobs' history; say so and ask
   * (EHQ-512).
   */
  private confirmDeleteHistory(jobCount: number): Observable<boolean> {
    const jobs = jobCount === 1 ? '1 job' : `${jobCount} jobs`;
    const they = jobCount === 1 ? 'it' : 'they';
    return this.dialogService.confirm({
      title: 'Delete Execution History',
      message: `This workflow and its routines have execution history from ${jobs}. Deleting the workflow also deletes that history and the files ${they} produced, and this can't be undone. Do you want to continue?`,
      confirmCaption: 'Delete',
      cancelCaption: 'Cancel',
      isDestructive: true,
    });
  }

  /**
   * The routines deleting `workflow` removes: those bound to it by id, and
   * those bound to its name unless the latest published version still has a
   * workflow of that name (`published`), which keeps them runnable (EHQ-536).
   */
  private getRoutinesConnectedToWorkflow(
    plans: ExecPlan[],
    workflow: Workflow,
    published: boolean,
  ): ExecPlan[] {
    return plans.filter(
      (plan) =>
        plan.workflowId === workflow.workflowId ||
        (!published && !!workflow.name && plan.workflowName === workflow.name),
    );
  }

  /**
   * Whether the project's latest published version has a workflow named
   * `name`, ignoring case, as the server decides it. False when there is no
   * published version, or the lookup fails: then the dialog warns about every
   * routine bound by name, which errs toward saying too much.
   */
  private latestPublishedHasWorkflow(projectId: number, name: string | undefined): Observable<boolean> {
    const latestVersionId = this.appStateService.project()?.latestVersionId;
    if (!latestVersionId || !name) {
      return of(false);
    }
    const lower = name.toLowerCase();
    return this.versionService.getProjectVersion(projectId, latestVersionId).pipe(
      map((version) => (version.workflows ?? []).some((w) => w.name?.toLowerCase() === lower)),
      catchError(() => of(false)),
    );
  }

  formatTimestamp(timestamp: string): string {
    return DateFormattingHelper.formatTimestamp(timestamp);
  }

  isSelectedWorkflow(workflow: Workflow): boolean {
    return workflow.workflowId === this.selectedWorkflowId();
  }

  isActiveWorkflow(workflow: Workflow): boolean {
    const activeId = this.activeWorkflowId();
    return activeId > 0 && workflow.workflowId === activeId;
  }

  getActiveWorkflowName(): string {
    const activeWorkflow = this.activeWorkflow();
    return activeWorkflow?.name || '';
  }

  onDocumentClick(event: Event): void {
    const hostElement = this.elementRef.nativeElement;
    if (!hostElement) {
      this.closeDropdown();
      return;
    }

    const target = event.target as Node | null;
    if (target && target === hostElement) {
      return;
    }

    try {
      if (!hostElement.contains(target as Node)) {
        this.closeDropdown();
      }
    } catch {
      if (target !== hostElement) {
        this.closeDropdown();
      }
    }
  }

  onEscapeKey(): void {
    this.closeDropdown();
  }

  onKeyDown(event: KeyboardEvent, action: string, workflow?: Workflow): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();

      switch (action) {
        case 'createNewWorkflow':
          this.createNewWorkflow();
          break;
        case 'selectWorkflow':
          if (workflow) {
            this.selectWorkflow(workflow);
          }
          break;
        case 'closeDropdown':
          this.closeDropdown();
          break;
      }
    }
  }
}
